/* global PaygentToken */

import { configService } from '../services/configService';

/**
 * PaygentToken.js を動的にロード
 */
export const loadPaygentScript = async () => {
  return new Promise(async (resolve, reject) => {
    // 既にロード済みの場合
    if (typeof PaygentToken !== 'undefined') {
      resolve();
      return;
    }

    // 既存のスクリプトタグをチェック
    const existingScript = document.querySelector('script[data-paygent-token]');
    if (existingScript) {
      existingScript.addEventListener('load', resolve);
      existingScript.addEventListener('error', reject);
      return;
    }

    try {
      // ✅ configServiceから動的に取得
      let scriptUrl = configService.getConfig('paygentApiUrl');
      
      // configServiceがまだ初期化されていない場合は初期化
      if (!scriptUrl) {
        await configService.fetchConfig();
        scriptUrl = configService.getConfig('paygentApiUrl');
      }

      // スクリプトを動的に追加
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.charset = 'UTF-8';
      script.src = scriptUrl;
      script.setAttribute('data-paygent-token', 'true');
      
      script.onload = () => {
        resolve();
      };
      
      script.onerror = () => {
        console.error('[PAYGENT] ❌ スクリプトのロード失敗:', scriptUrl);
        reject(new Error('PaygentToken.jsのロードに失敗しました'));
      };

      document.head.appendChild(script);
    } catch (error) {
      console.error('[PAYGENT] ❌ Error loading script:', error);
      reject(error);
    }
  });
};

/**
 * Paygent設定を取得
 */
export const getPaygentConfig = async () => {
  try {
    // ✅ configServiceから動的に取得
    let merchantId = configService.getConfig('paygentMerchantId');
    let tokenKey = configService.getConfig('paygentTokenKey');
    
    // configServiceがまだ初期化されていない場合は初期化
    if (!merchantId || !tokenKey) {
      await configService.fetchConfig();
      merchantId = configService.getConfig('paygentMerchantId');
      tokenKey = configService.getConfig('paygentTokenKey');
    }

    if (!merchantId || !tokenKey) {
      throw new Error('Paygent設定が不足しています。環境変数を確認してください。');
    }

    return {
      merchantId,
      tokenKey
    };
  } catch (error) {
    console.error('[PAYGENT] ❌ Error getting config:', error);
    throw error;
  }
};

/**
 * PaygentTokenの存在確認
 */
export const validatePaygentToken = () => {
  if (typeof PaygentToken === 'undefined') {
    console.error('[PAYGENT] ❌ PaygentToken is undefined');
    throw new Error('PaygentToken.jsがロードされていません');
  }
};

/**
 * Paygent APIリクエストパラメータを構築
 */
export const buildPaygentParams = (cardData) => {
  const { cardNumber, cardholderName, expiryMonth, expiryYear, cvv } = cardData;

  return {
    card_number: cardNumber.replace(/\s/g, ''),
    expire_year: expiryYear,
    expire_month: expiryMonth.padStart(2, '0'),
    cvc: cvv,
    name: cardholderName
  };
};

/**
 * Paygentレスポンスを処理
 */
export const handlePaygentResponse = (response, resolve, reject) => {
  if (response.result === '0000') {
    const result = {
      token: response.tokenizedCardObject.token,
      maskedCardNumber: response.tokenizedCardObject.masked_card_number,
      validUntil: response.tokenizedCardObject.valid_until
    };
    
    resolve(result);
  } else {
    console.error('[PAYGENT] ❌ トークン生成エラー. result:', response.result);
    console.error('[PAYGENT] Full error response:', response);
    reject(new Error(`トークン生成エラー: ${response.result}`));
  }
};

/**
 * PaygentトークンAPIを呼び出し
 */
export const callPaygentTokenApi = (config, params, resolve, reject) => {
  try {
    validatePaygentToken();
    const paygentToken = new PaygentToken();

    paygentToken.createToken(
      config.merchantId,
      config.tokenKey,
      params,
      (response) => {
        handlePaygentResponse(response, resolve, reject);
      }
    );
  } catch (error) {
    console.error('[PAYGENT] ❌ 例外エラー:', error);
    reject(error);
  }
};

/**
 * Paygentトークン生成(メイン)
 */
export const generatePaygentToken = async (cardData) => {
  try {
    // ① スクリプトを動的にロード
    await loadPaygentScript();
    
    // ② 設定を取得
    const config = await getPaygentConfig();
    
    // ③ トークン生成
    return new Promise((resolve, reject) => {
      // リクエストパラメータを構築
      const params = buildPaygentParams(cardData);
      
      // API呼び出し
      callPaygentTokenApi(config, params, resolve, reject);
    });
  } catch (error) {
    console.error('[PAYGENT] ❌❌❌ Token generation failed ❌❌❌');
    console.error('[PAYGENT] Error type:', error.constructor.name);
    console.error('[PAYGENT] Error message:', error.message);
    console.error('[PAYGENT] Error stack:', error.stack);
    throw error;
  }
};

/**
 * PaygentToken.jsのロード確認
 */
export const checkPaygentTokenLoaded = () => {
  return typeof PaygentToken !== 'undefined';
};

/**
 * 3DS画面をdocument.write()で表示
 * 以降はバックエンドが自動で処理(認証完了後、クラウドファンクションでログイン画面に遷移)
 * @param {string} redirectHtml - 3DS認証用HTML
 */
export const display3DSScreen = (redirectHtml) => {
  // 現在のページを3DS画面に完全に置き換える
  document.open();
  document.write(redirectHtml);
  document.close();
  
  // フロントエンドでの処理はここで終了
  // 以降はバックエンドが全て自動処理
};